import React, { useState } from 'react';
import { CheckCircle2, Lock, AlertCircle, ChevronDown, ChevronUp, ShoppingCart } from 'lucide-react';
import { FormSelect } from '../FormSelect';
interface OrderRequestFormProps {
  productName: string;
  productSlug: string;
  restricted?: boolean;
}
export function OrderRequestForm({
  productName,
  productSlug,
  restricted = false
}: OrderRequestFormProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    orgName: '',
    country: '',
    deliveryCity: '',
    quantity: '',
    timeframe: '',
    notes: '',
    eligibility: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value
    });
  };
  if (submitted) {
    return <div className="bg-green-50 border border-green-200 p-6 sm:p-8 rounded-sm text-center mt-8 animate-in fade-in zoom-in duration-300" data-id="element-161">
        <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" data-id="element-162" />
        <h3 className="text-lg sm:text-xl font-bold text-navy-900 mb-2" data-id="element-163">
          Request Received
        </h3>
        <p className="text-sm text-steel-600 mb-6 max-w-md mx-auto" data-id="element-164">
          Thank you. Your order request for{' '}
          <span className="font-bold" data-id="element-165">{productName}</span> has been received. We
          will confirm next steps.
        </p>
        <button onClick={() => setSubmitted(false)} className="text-sm font-bold text-navy-900 underline hover:text-cg-orange-600" data-id="element-166">
          Submit another request
        </button>
      </div>;
  }
  return <div className="mt-8 border-t border-steel-200 pt-8" data-id="element-167">
      {!isExpanded ? <button onClick={() => setIsExpanded(true)} className="w-full max-w-2xl mx-auto flex items-center justify-center gap-3 bg-cg-orange-600 hover:bg-cg-orange-500 text-white font-bold uppercase tracking-wider py-4 rounded-sm transition-all shadow-sm hover:shadow-md group" data-id="element-168">
          <ShoppingCart className="w-5 h-5" data-id="element-169" />
          Request an Order
          <ChevronDown className="w-5 h-5 group-hover:translate-y-0.5 transition-transform" data-id="element-170" />
        </button> : <div className="max-w-2xl mx-auto bg-slate-50 border border-steel-200 rounded-sm overflow-hidden animate-in slide-in-from-top-4 duration-300" data-id="element-171">
          <div className="bg-white border-b border-steel-200 px-6 py-4 flex items-center justify-between" data-id="element-172">
            <h3 className="text-lg font-bold text-navy-900 flex items-center gap-2" data-id="element-173">
              <ShoppingCart className="w-5 h-5 text-cg-orange-600" data-id="element-174" />
              Request Order
            </h3>
            <button onClick={() => setIsExpanded(false)} className="text-steel-400 hover:text-navy-900 transition-colors" data-id="element-175">
              <ChevronUp className="w-5 h-5" data-id="element-176" />
            </button>
          </div>

          <div className="p-6 sm:p-8" data-id="element-177">
            {restricted && <div className="mb-6 bg-red-50 border border-red-200 p-4 rounded-sm flex items-start gap-3" data-id="element-178">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" data-id="element-179" />
                <div data-id="element-180">
                  <p className="text-sm font-bold text-red-800 mb-1" data-id="element-181">
                    Restricted Item
                  </p>
                  <p className="text-xs text-red-700" data-id="element-182">
                    Verification of eligibility and end-user documentation will
                    be required before order processing.
                  </p>
                </div>
              </div>}

            <form onSubmit={handleSubmit} className="space-y-5" data-id="element-183">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-184">
                <div data-id="element-185">
                  <label htmlFor="firstName" className="block text-xs font-bold text-navy-900 mb-1.5" data-id="element-186">
                    First Name *
                  </label>
                  <input type="text" id="firstName" required className="w-full bg-white border border-steel-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-cg-orange-600 focus:ring-1 focus:ring-cg-orange-600 transition-shadow" value={formData.firstName} onChange={handleChange} data-id="element-187" />
                </div>
                <div data-id="element-188">
                  <label htmlFor="lastName" className="block text-xs font-bold text-navy-900 mb-1.5" data-id="element-189">
                    Last Name *
                  </label>
                  <input type="text" id="lastName" required className="w-full bg-white border border-steel-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-cg-orange-600 focus:ring-1 focus:ring-cg-orange-600 transition-shadow" value={formData.lastName} onChange={handleChange} data-id="element-190" />
                </div>
              </div>

              <div data-id="element-191">
                <label htmlFor="email" className="block text-xs font-bold text-navy-900 mb-1.5" data-id="element-192">
                  Work Email *
                </label>
                <input type="email" id="email" required className="w-full bg-white border border-steel-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-cg-orange-600 focus:ring-1 focus:ring-cg-orange-600 transition-shadow" value={formData.email} onChange={handleChange} data-id="element-193" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-194">
                <div data-id="element-195">
                  <label htmlFor="orgName" className="block text-xs font-bold text-navy-900 mb-1.5" data-id="element-196">
                    Organisation *
                  </label>
                  <input type="text" id="orgName" required className="w-full bg-white border border-steel-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-cg-orange-600 focus:ring-1 focus:ring-cg-orange-600 transition-shadow" value={formData.orgName} onChange={handleChange} data-id="element-197" />
                </div>
                <div data-id="element-198">
                  <label htmlFor="country" className="block text-xs font-bold text-navy-900 mb-1.5" data-id="element-199">
                    Country *
                  </label>
                  <input type="text" id="country" required className="w-full bg-white border border-steel-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-cg-orange-600 focus:ring-1 focus:ring-cg-orange-600 transition-shadow" value={formData.country} onChange={handleChange} data-id="element-200" />
                </div>
              </div>

              <div data-id="element-201">
                <label htmlFor="deliveryCity" className="block text-xs font-bold text-navy-900 mb-1.5" data-id="element-202">
                  Delivery Postcode / City
                </label>
                <input type="text" id="deliveryCity" className="w-full bg-white border border-steel-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-cg-orange-600 focus:ring-1 focus:ring-cg-orange-600 transition-shadow" placeholder="Optional" value={formData.deliveryCity} onChange={handleChange} data-id="element-203" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-204">
                <div data-id="element-205">
                  <label htmlFor="quantity" className="block text-xs font-bold text-navy-900 mb-1.5" data-id="element-206">
                    Quantity *
                  </label>
                  <input type="text" id="quantity" required className="w-full bg-white border border-steel-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-cg-orange-600 focus:ring-1 focus:ring-cg-orange-600 transition-shadow" placeholder="e.g. 50" value={formData.quantity} onChange={handleChange} data-id="element-207" />
                </div>
                <div data-id="element-208">
                  <label htmlFor="timeframe" className="block text-xs font-bold text-navy-900 mb-1.5" data-id="element-209">
                    Timeframe
                  </label>
                  <div className="relative" data-id="element-210">
                    <select id="timeframe" className="w-full appearance-none bg-white border border-steel-300 rounded-sm px-3 py-2.5 pr-10 text-sm focus:outline-none focus:border-cg-orange-600 focus:ring-1 focus:ring-cg-orange-600 transition-shadow text-navy-900" value={formData.timeframe} onChange={handleChange} data-id="element-211">
                      <option value="" data-id="element-212">Select...</option>
                      <option value="Immediate" data-id="element-213">Immediate</option>
                      <option value="1-4 weeks" data-id="element-214">1-4 weeks</option>
                      <option value="1-3 months" data-id="element-215">1-3 months</option>
                      <option value="3+ months" data-id="element-216">3+ months</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-steel-500" data-id="element-217">
                      <ChevronDown className="w-4 h-4" data-id="element-218" />
                    </div>
                  </div>
                </div>
              </div>

              <div data-id="element-219">
                <label htmlFor="notes" className="block text-xs font-bold text-navy-900 mb-1.5" data-id="element-220">
                  Notes
                </label>
                <textarea id="notes" rows={3} className="w-full bg-white border border-steel-300 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-cg-orange-600 focus:ring-1 focus:ring-cg-orange-600 resize-none transition-shadow" placeholder="Include any sizing, colour, or configuration requirements" value={formData.notes} onChange={handleChange} data-id="element-221" />
              </div>

              <div className="pt-2" data-id="element-222">
                <button type="submit" className="w-full bg-cg-orange-600 hover:bg-cg-orange-500 text-white font-bold uppercase tracking-wider py-3.5 rounded-sm transition-colors text-sm shadow-sm" data-id="element-223">
                  Submit Request
                </button>
              </div>

              <div className="flex items-center gap-2 justify-center text-steel-400 pt-2" data-id="element-224">
                <Lock className="w-3 h-3 flex-shrink-0" data-id="element-225" />
                <span className="text-[10px]" data-id="element-226">
                  Secure submission. No payment taken at this stage.
                </span>
              </div>
            </form>
          </div>
        </div>}
    </div>;
}